/**
 * Servicio de las solicitudes del Portal del Empleado (lado dashboard).
 *
 * Las solicitudes (permiso/vacaciones/dieta/comunicado; anticipos quedan
 * fuera) las crea el trabajador desde la app en
 *   servicios/{servicioId}/vigilantes/{userId}/solicitudes/{id}
 * y el inspector/administrativo/administrador las gestiona desde aquí:
 * acepta/rechaza (permiso/vacaciones/dieta), cierra (comunicado) y chatea
 * en la subcolección `mensajes`. El trigger `notificarSolicitud` (Admin SDK)
 * envía los emails al trabajador al margen de estas escrituras.
 */
import {
  collection, collectionGroup, doc, addDoc, updateDoc, onSnapshot,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../../firebase';

// Límite de los adjuntos del chat (mismo criterio que gestión documental).
const MAX_ADJUNTO_BYTES = 20 * 1024 * 1024;

// Anticipos NO se procesan en Sentry → se excluyen de todas las vistas.
export const TIPO_EXCLUIDO = 'anticipo';

// accion del botón → estado destino de la solicitud.
const ESTADO_POR_ACCION = {
  aceptar: 'aceptada',
  rechazar: 'rechazada',
  cerrar: 'cerrada',
};

/**
 * Escucha en tiempo real las solicitudes de un vigilante (excluye anticipos),
 * ordenadas por fecha de creación descendente.
 * @param {string} servicioId Servicio.
 * @param {string} userId Doc id del vigilante.
 * @param {function(Array<Object>):void} cb Callback con las solicitudes.
 * @param {function(Error):void} [onError] Callback de error.
 * @return {function():void} Unsubscribe.
 */
export function escucharSolicitudesVigilante(servicioId, userId, cb, onError) {
  const ref = collection(
    db, 'servicios', servicioId, 'vigilantes', userId, 'solicitudes');
  return onSnapshot(query(ref, orderBy('creadoEn', 'desc')), (snap) => {
    const docs = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((s) => s.tipo !== TIPO_EXCLUIDO);
    cb(docs);
  }, onError || (() => {}));
}

/**
 * Escucha los mensajes (chat) de una solicitud, en orden cronológico.
 * @param {string} servicioId Servicio.
 * @param {string} userId Doc id del vigilante.
 * @param {string} solicitudId Doc id de la solicitud.
 * @param {function(Array<Object>):void} cb Callback con los mensajes.
 * @return {function():void} Unsubscribe.
 */
export function escucharMensajes(servicioId, userId, solicitudId, cb) {
  const ref = collection(
    db, 'servicios', servicioId, 'vigilantes', userId,
    'solicitudes', solicitudId, 'mensajes');
  return onSnapshot(query(ref, orderBy('creadoEn', 'asc')), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, () => {});
}

/**
 * Sube un archivo del chat a Storage (convención compartida con las apps:
 * `servicios/{svc}/solicitudes/{userId}/{solicitudId}/chat/…`).
 * @param {string} servicioId Servicio.
 * @param {string} userId Doc id del vigilante.
 * @param {string} solicitudId Doc id de la solicitud.
 * @param {File} file Fichero del input.
 * @return {Promise<Object>} { nombre, url, contentType, size, subidoEn }.
 */
export async function subirAdjuntoChat(servicioId, userId, solicitudId, file) {
  if (file.size > MAX_ADJUNTO_BYTES) {
    throw new Error(`"${file.name}" supera el límite de 20 MB.`);
  }
  const ts = Date.now();
  const nombreSaneado = String(file.name || 'adjunto')
    .replace(/[^\w.-]+/g, '_');
  const path = `servicios/${servicioId}/solicitudes/${userId}/` +
    `${solicitudId}/chat/${ts}_${nombreSaneado}`;
  const objRef = storageRef(storage, path);
  await uploadBytes(objRef, file, {
    contentType: file.type || 'application/octet-stream',
  });
  const url = await getDownloadURL(objRef);
  return {
    nombre: file.name,
    url,
    contentType: file.type || '',
    size: file.size || 0,
    subidoEn: ts,
  };
}

/**
 * Publica un mensaje del inspector en el chat (dispara email al empleado).
 * @param {string} servicioId Servicio.
 * @param {string} userId Doc id del vigilante.
 * @param {string} solicitudId Doc id de la solicitud.
 * @param {string} texto Texto del mensaje.
 * @param {string} autorEmail Email del autor (para auditoría).
 * @param {Array<Object>} [adjuntos] Adjuntos ya subidos (subirAdjuntoChat).
 * @return {Promise<void>}
 */
export async function enviarMensajeInspector(
  servicioId, userId, solicitudId, texto, autorEmail, adjuntos = []) {
  const ref = collection(
    db, 'servicios', servicioId, 'vigilantes', userId,
    'solicitudes', solicitudId, 'mensajes');
  await addDoc(ref, {
    autor: 'inspector',
    texto: texto || '',
    adjuntos: Array.isArray(adjuntos) ? adjuntos : [],
    creadoPor: autorEmail || '',
    creadoEn: serverTimestamp(),
  });
}

/**
 * Resuelve una solicitud (aceptar/rechazar/cerrar). Escribe estado +
 * resolucion; el trigger notifica al trabajador.
 * @param {string} servicioId Servicio.
 * @param {string} userId Doc id del vigilante.
 * @param {string} solicitudId Doc id de la solicitud.
 * @param {('aceptar'|'rechazar'|'cerrar')} accion Acción.
 * @param {Object} [opts] {motivo, autorEmail, rol}.
 * @return {Promise<void>}
 */
export async function resolverSolicitud(
  servicioId, userId, solicitudId, accion, opts = {}) {
  const estado = ESTADO_POR_ACCION[accion];
  if (!estado) throw new Error(`Acción no válida: ${accion}`);
  await updateDoc(
    doc(db, 'servicios', servicioId, 'vigilantes', userId,
      'solicitudes', solicitudId),
    {
      estado,
      resolucion: {
        accion: estado,
        por: opts.autorEmail || '',
        rol: opts.rol || '',
        motivo: opts.motivo || '',
        en: serverTimestamp(),
      },
    });
}

/**
 * Escucha en tiempo real TODAS las solicitudes del servicio (bandeja global
 * del dashboard), de cualquier estado, excluyendo anticipos. El split en
 * pendientes/resueltas y el filtrado por scope de rol se hacen en el
 * componente (campos denormalizados del doc).
 * @param {string} servicioId Servicio.
 * @param {function(Array<Object>):void} cb Callback con las solicitudes.
 * @param {function(Error):void} [onError] Callback de error.
 * @return {function():void} Unsubscribe.
 */
export function escucharSolicitudesServicio(servicioId, cb, onError) {
  const q = query(
    collectionGroup(db, 'solicitudes'),
    where('servicioId', '==', servicioId),
    orderBy('creadoEn', 'desc'));
  return onSnapshot(q, (snap) => {
    const docs = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((s) => s.tipo !== TIPO_EXCLUIDO);
    cb(docs);
  }, onError || (() => {}));
}

/**
 * Catálogo (cacheado) de request types del Portal RRHH para trasladar un
 * comunicado, con prefill por tipo si se pasa el comunicado. Callable server.
 * @param {string} servicioId Servicio.
 * @param {Object} [opts] {vigilanteDocId, solicitudId, force}.
 * @return {Promise<Object>} {serviceDeskId, areaPorDefecto, requestTypes,
 *   prefills}.
 */
export async function getCatalogoTrasladoRRHH(servicioId, opts = {}) {
  const fn = httpsCallable(functions, 'getCatalogoTrasladoRRHH');
  const res = await fn({
    servicioId,
    vigilanteDocId: opts.vigilanteDocId,
    solicitudId: opts.solicitudId,
    force: !!opts.force,
  });
  return res.data;
}

/**
 * Traslada un comunicado a RRHH creando el ticket de Jira del request type
 * elegido con los campos rellenados. Callable server.
 * @param {Object} payload {servicioId, vigilanteDocId, solicitudId,
 *   requestTypeId, campos}.
 * @return {Promise<Object>} {issueKey, requestTypeName}.
 */
export async function trasladarSolicitudRRHH(payload) {
  const fn = httpsCallable(functions, 'trasladarSolicitudRRHH');
  const res = await fn(payload);
  return res.data;
}
